import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface BlogPost {
  id: string;
  title: string | null;
  slug: string | null;
  content: string | null;
  meta_description: string | null;
  keyword: string | null;
  featured_image: string | null;
  is_pillar: boolean;
  published_at: string | null;
  canonical_url: string | null;
}

interface ClientInfo {
  business_type: string | null;
  location: string | null;
  website_url: string | null;
}

const stripHtml = (html: string, max = 220) => {
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > max ? text.slice(0, max) + "…" : text;
};

const Demo = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      const [{ data: postsData }, { data: clientData }] = await Promise.all([
        supabase
          .from("blog_posts")
          .select("id,title,slug,content,meta_description,keyword,featured_image,is_pillar,published_at,canonical_url")
          .eq("client_id", clientId)
          .order("is_pillar", { ascending: false })
          .order("published_at", { ascending: false }),
        supabase
          .from("clients")
          .select("business_type,location,website_url")
          .eq("id", clientId)
          .maybeSingle(),
      ]);
      setPosts(postsData || []);
      setClient(clientData || null);
      setLoading(false);
    })();
  }, [clientId]);

  return (
    <div className="min-h-screen bg-[#08090d] text-white">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <p className="text-xs font-mono text-[#00e5a0] tracking-[0.3em] uppercase mb-4">
            Your SEO Demo
          </p>
          <h1 className="text-3xl sm:text-5xl font-bold mb-4">
            {client?.business_type ? (
              <>
                Custom pages for{" "}
                <span style={{ color: "#00e5a0" }}>{client.business_type}</span>
                {client.location ? ` in ${client.location}` : ""}
              </>
            ) : (
              "Your custom SEO pages"
            )}
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto text-base sm:text-lg">
            We generated {posts.length} {posts.length === 1 ? "page" : "pages"} optimized to rank for your top opportunities. This is just the start — your full engine publishes new content every week.
          </p>
        </div>
      </header>

      {/* Posts grid */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        {loading ? (
          <div className="text-center text-white/40 font-mono text-sm py-20">
            Loading your demo…
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center text-white/40 font-mono text-sm py-20">
            No pages generated yet. Check back in a couple of minutes.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => {
              const href = post.canonical_url || (post.slug ? `#${post.slug}` : "#");
              return (
              <a
                key={post.id}
                href={href}
                target={post.canonical_url ? "_blank" : undefined}
                rel={post.canonical_url ? "noopener noreferrer" : undefined}
                className="group block bg-[#0f1015] border border-white/5 rounded-xl overflow-hidden hover:border-[#00e5a0]/40 hover:shadow-[0_0_40px_rgba(0,229,160,0.12)] hover:-translate-y-0.5 transition-all"
              >
                {post.featured_image && (
                  <div className="aspect-[16/9] overflow-hidden bg-black/40">
                    <img
                      src={post.featured_image}
                      alt={post.title || "Featured"}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {post.is_pillar && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-[#00e5a0]/15 text-[#00e5a0] border border-[#00e5a0]/20">
                        Pillar
                      </span>
                    )}
                    {post.keyword && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-white/5 text-white/60 border border-white/10">
                        {post.keyword}
                      </span>
                    )}
                  </div>
                  <h2 className="text-lg font-bold text-white mb-2 leading-snug line-clamp-2 group-hover:text-[#00e5a0] transition-colors">
                    {post.title || "Untitled"}
                  </h2>
                  <p className="text-sm text-white/50 leading-relaxed line-clamp-3 mb-4">
                    {post.meta_description ||
                      (post.content ? stripHtml(post.content) : "")}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-mono text-[#00e5a0]/80 group-hover:text-[#00e5a0]">
                    View live page →
                  </span>
                </div>
              </a>
              );
            })}
          </div>
        )}
      </main>

      {/* CTA */}
      <section className="border-t border-white/5 py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-bold text-white mb-6">
            Is your business ready for this?
          </h2>
          <p className="text-white/60 mb-8 text-base sm:text-lg">
            Every day we publish more pages. Every day more customers find you.
          </p>
          <Link
            to="/#pricing"
            className="inline-block px-10 py-5 rounded-lg font-bold text-base"
            style={{
              background: "linear-gradient(135deg, #00e5a0, #00c98a)",
              color: "#052e16",
              boxShadow: "0 0 40px rgba(0,229,160,0.4)",
              animation: "gentlePulse 2.5s ease-in-out infinite",
            }}
          >
            Start Growing Today →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Demo;
